import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, getStoredUser, getCurrentUser } from '../../utils/auth';
import { apiRequest } from '../../utils/api';
import { useMessageNotifications } from '../../hooks/useMessageNotifications';

const AdminLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [organization, setOrganization] = useState(null);
    
    // Get organization ID from URL
    const organizationId = location.pathname.match(/\/organizations\/(\d+)/)?.[1];
    
    // Message notifications hook
    const {
        unreadCount,
        showNotification,
        notificationMessage,
        handleGoToMessages,
        handleCloseNotification
    } = useMessageNotifications(organizationId);

    useEffect(() => {
        loadUser();
    }, []);

    useEffect(() => {
        loadOrganization();
    }, [location.pathname]);

    const loadUser = async () => {
        const storedUser = getStoredUser();
        if (storedUser) {
            setUser(storedUser);
        } else {
            const currentUser = await getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
            } else {
                navigate('/admin/login');
            }
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const loadOrganization = async () => {
        const orgId = location.pathname.match(/\/organizations\/(\d+)/)?.[1];
        if (orgId) {
            try {
                const response = await apiRequest(`/organizations/${orgId}`);
                setOrganization(response.data || null);
            } catch (error) {
                console.error('Load organization error:', error);
                setOrganization(null);
            }
        } else {
            setOrganization(null);
        }
    };

    // Icon component helper
    const Icon = ({ name, className = "w-5 h-5" }) => {
        const icons = {
            dashboard: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
            ),
            categories: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
            ),
            products: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
            ),
            banners: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
            stories: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            ),
            campaigns: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
            ),
            votes: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            reactions: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
            ),
            messages: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            ),
            analytics: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            search: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            ),
            settings: (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            )
        };
        return icons[name] || null;
    };

    const getMenuItems = () => {
        const orgId = location.pathname.match(/\/organizations\/(\d+)/)?.[1];
        const basePath = orgId ? `/admin/organizations/${orgId}` : '/admin';
        
        return [
            { name: 'Dashboard', path: `${basePath}/dashboard`, icon: 'dashboard' },
            { name: 'Kategoriler', path: `${basePath}/categories`, icon: 'categories' },
            { name: 'Ürünler', path: `${basePath}/products`, icon: 'products' },
            { name: 'Bannerlar', path: `${basePath}/banners`, icon: 'banners' },
            { name: 'Stories', path: `${basePath}/stories`, icon: 'stories' },
            { name: 'Kampanyalar', path: `${basePath}/campaigns`, icon: 'campaigns' },
            { name: 'Oylamalar', path: `${basePath}/votes`, icon: 'votes' },
            { name: 'Beğeniler', path: `${basePath}/reactions`, icon: 'reactions' },
            { 
                name: 'Mesajlar', 
                path: `${basePath}/messages`, 
                icon: 'messages',
                badge: unreadCount > 0 ? unreadCount : null
            },
            { name: 'İstatistikler', path: `${basePath}/analytics`, icon: 'analytics' },
            { name: 'Arama Trendleri', path: `${basePath}/search-analytics`, icon: 'search' },
            { name: 'Ayarlar', path: `${basePath}/settings`, icon: 'settings' }
        ];
    };

    const menuItems = getMenuItems();

    const isActive = (path) => {
        return location.pathname === path;
    };

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar - Always visible, responsive */}
            <div className={`fixed lg:static inset-y-0 left-0 z-50 w-56 bg-gray-900 text-white transition-transform duration-300 flex flex-col ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between h-14 px-4 border-b border-gray-800 shrink-0">
                    <h1 className="text-lg font-bold truncate">Zehuma QR</h1>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-gray-400 hover:text-white"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable Menu */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2">
                    {menuItems.map((item) => (
                        <a
                            key={item.path}
                            href={item.path}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(item.path);
                                // Close sidebar on mobile after navigation
                                if (window.innerWidth < 1024) {
                                    setSidebarOpen(false);
                                }
                            }}
                            className={`flex items-center space-x-3 px-4 py-2.5 mx-2 my-1 rounded-lg transition relative ${
                                isActive(item.path)
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            }`}
                        >
                            <Icon name={item.icon} className="w-5 h-5 shrink-0" />
                            <span className="text-sm font-medium truncate flex-1">{item.name}</span>
                            {item.badge && item.badge > 0 && (
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center ${
                                    isActive(item.path)
                                        ? 'bg-white text-blue-600'
                                        : 'bg-red-500 text-white'
                                }`}>
                                    {item.badge}
                                </span>
                            )}
                        </a>
                    ))}
                </nav>

                {/* User Info - Fixed at bottom */}
                {user && (
                    <div className="border-t border-gray-800 p-3 shrink-0">
                        <div className="flex items-center space-x-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                                <span className="text-white text-xs font-semibold">
                                    {user.first_name?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate">
                                    {user.first_name || ''} {user.last_name || ''}
                                </p>
                                <p className="text-xs text-gray-400 truncate">{user.email || 'No email'}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="w-full px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-xs transition"
                        >
                            Çıkış Yap
                        </button>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top Bar */}
                <header className="bg-white shadow-sm sticky top-0 z-40 shrink-0">
                    <div className="flex items-center justify-between h-14 px-4 sm:px-6 lg:px-8">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="text-gray-500 hover:text-gray-700 lg:hidden"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <div className="flex-1 lg:ml-0"></div>
                        <div className="flex items-center space-x-4">
                            {organization?.slug && (
                                <a
                                    href={`https://menu.zehuma.com/${organization.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md hover:shadow-lg"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    <span className="font-medium">Menüyü Görüntüle</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    {children}
                </main>
            </div>

            {/* Sidebar Overlay (Mobile only) */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Message Notification Popup */}
            {showNotification && (
                <div className="fixed top-4 right-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-2xl border-2 border-orange-500 p-4 max-w-sm">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center animate-pulse">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-bold text-gray-900 mb-1">Yeni Mesaj!</h3>
                                <p className="text-sm text-gray-600 mb-3">{notificationMessage}</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleGoToMessages}
                                        className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition font-medium text-sm shadow-md"
                                    >
                                        Mesajlara Git
                                    </button>
                                    <button
                                        onClick={handleCloseNotification}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm"
                                    >
                                        Kapat
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseNotification}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLayout;

